import { useTranslation } from 'react-i18next';
import Card from '@/components/base/Card';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import type { LaundryReviewsResult } from '@/services/reviewsService';
import ReviewCard from './ReviewCard';
import StarsRating from './StarsRating';

interface LaundryReviewsPanelProps {
  data: LaundryReviewsResult | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

export default function LaundryReviewsPanel({
  data,
  loading,
  error,
  onRetry,
}: LaundryReviewsPanelProps) {
  const { t } = useTranslation();

  if (error) {
    return (
      <ErrorState
        title={t('reviews.error.laundry')}
        description={t('common.errorText')}
        retryLabel={t('common.retry')}
        onRetry={onRetry}
      />
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Card className="flex flex-wrap items-center justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-4 w-60" />
          </div>
          <div className="space-y-2">
            <Skeleton className="ms-auto h-8 w-24" />
            <Skeleton className="ms-auto h-5 w-32" />
          </div>
        </Card>
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const summary = data?.summary ?? { average: null, count: 0 };
  const reviews = data?.reviews ?? [];

  return (
    <div className="space-y-5">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground-950">{t('reviews.overall.title')}</h2>
            <p className="mt-1 text-sm text-foreground-500">{t('reviews.overall.caption')}</p>
          </div>

          {summary.average === null ? (
            <div className="text-end">
              <StarsRating value={0} size="lg" />
              <p className="mt-1 text-xs font-medium text-foreground-500">
                {t('reviews.overall.none')}
              </p>
            </div>
          ) : (
            <div className="text-end">
              <div className="flex items-end justify-end gap-1.5">
                <span className="text-3xl font-bold leading-none text-foreground-950">
                  {summary.average.toFixed(1)}
                </span>
                <span className="text-sm text-foreground-400">{t('reviews.overall.outOf')}</span>
              </div>
              <StarsRating value={summary.average} size="md" className="mt-1.5" />
              <p className="mt-1 text-xs text-foreground-500">
                {t('reviews.overall.reviewsCount')}: {summary.count}
              </p>
            </div>
          )}
        </div>
      </Card>

      <section>
        <h3 className="mb-3 text-sm font-semibold text-foreground-950">{t('reviews.list.title')}</h3>
        {reviews.length === 0 ? (
          <EmptyState
            icon="ri-star-line"
            title={t('reviews.list.empty')}
            description={t('reviews.list.emptyDesc')}
          />
        ) : (
          <div className="space-y-3">
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}